import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import {
  api,
  type AiRequest,
  type AttachmentKind,
  type ChatSummary,
  type DeleteScope,
  type MessageDto,
  type OutgoingAlbum,
  type OutgoingMedia,
  type ReactionView,
  type ReplyTo,
} from '../api/client.ts';
import { chatSocket, type ServerEvent } from '../api/socket.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { pinnedFirst } from '../lib/chatList.ts';
import { isOnScreen } from '../lib/onScreen.ts';
import {
  clearNotifications,
  drainNativeNotifications,
  reportActiveConversation,
  syncSubscription,
  watchNativePushEvents,
} from '../lib/push.ts';
import { DELETED_QUOTE, messagePreview, quoteMessage, quotePreview } from '../lib/reply.ts';
import {
  configureNativeShare,
  drainNativeShares,
  releaseNativeShare,
} from '../lib/nativeShare.ts';
import type { SharedPayload } from '../lib/share.ts';
import { TYPING_HEARTBEAT_MS, TYPING_TTL_MS, TypingReporter } from '../lib/typing.ts';
import * as outbox from './outbox.ts';

export type MessageStatus = 'sending' | 'sent' | 'read' | 'failed';

export interface MessageView extends MessageDto {
  /** 'sending' exists only on this client; the server knows sent and read. */
  status: MessageStatus;
  /** Arrived over the live socket in this session — drives the «только что» cue. */
  live?: boolean;
  /**
   * How far through `sendPhase` this message is, 0–100. Only ever set while
   * `status` is 'sending', and only for the kinds big enough for the wait to be
   * worth drawing.
   */
  progressPct?: number;
  /**
   * Which of the two waits the bubble is in, so it can say which.
   *
   * A picked video is re-encoded on this device before a byte goes up (see
   * lib/videoCompress.ts), and those seconds are not upload seconds — a bar that
   * called them one would be counting the network while the radio is idle.
   * Absent means uploading, which is what every other kind of message does and
   * all any of them ever did.
   */
  sendPhase?: 'compressing' | 'uploading';
  /**
   * An album's tiles as they are on this device, for as long as it is going up.
   *
   * Without them the sender would watch nine grey squares count to 100%, having
   * just chosen nine photographs. `attachments` takes over the moment the server
   * answers, and these are let go of then — see sendAlbum.
   */
  localItems?: { previewUrl: string | null; kind: AttachmentKind; durationMs: number | null }[];
}

/**
 * Whether a message can be answered at all.
 *
 * A call is a record of something that happened rather than something anybody
 * said, and its bubble is already a button that places the same call again. The
 * other exclusion is a bubble still on its way up: its id is this device's
 * invention until the POST returns, and a quote pointing at one would be
 * dropped by the server (replyTarget in server/src/chats.ts).
 */
export function canReplyTo(message: MessageView): boolean {
  return message.kind !== 'call' && message.status !== 'sending' && message.status !== 'failed';
}

interface ChatState {
  chats: ChatSummary[];
  chatsLoading: boolean;
  /**
   * The list could not be fetched, and there is nothing held from before to go
   * on showing. Distinct from an empty list, which is a fact about the account
   * — «Пока нет чатов» is a true sentence in one case and a lie in the other,
   * and on a phone opened from a notification the lie is the whole screen.
   */
  chatsFailed: boolean;
  /** Asks for the list again. What «Повторить» presses. */
  retryChats: () => void;
  connected: boolean;
  activeChatId: string | null;
  setActiveChatId: (id: string | null) => void;
  messages: MessageView[];
  messagesLoading: boolean;
  /**
   * Whether a page for the open conversation has ever landed on this device.
   *
   * The difference between «пусто» and «ещё не пришло», which an empty
   * `messages` cannot tell apart — and the thread drew the same nothing for
   * both, so a conversation opened on a bad signal looked exactly like one
   * nobody had ever written in. Held per chat rather than as a flag on the
   * fetch, because `messagesLoading` is about a request and this is about the
   * thread: walking back into a chat starts a new request over a page that is
   * already there, and that must not put the screen back to a spinner.
   */
  messagesReady: boolean;
  /**
   * The open conversation's newest page did not arrive. The thread is empty
   * because nothing was fetched, not because nothing was ever said in it.
   */
  messagesFailed: boolean;
  /** Fetches that page again. */
  retryMessages: () => void;
  /** Whether the open thread has history above what has been loaded. */
  hasMoreMessages: boolean;
  /** A page of that history is on its way. */
  loadingOlderMessages: boolean;
  /** Fetches the page before the oldest message on screen; safe to call twice. */
  loadOlderMessages: () => void;
  /**
   * Whether the open thread is a window rather than the end of the
   * conversation — true only after the search screen has opened a match that
   * was further back than the pages on screen.
   */
  hasNewerMessages: boolean;
  /** Fetches the page after the newest message on screen; safe to call twice. */
  loadNewerMessages: () => void;
  /**
   * Puts the thread on one message, fetching a window around it when it is not
   * already loaded. What a tap on a search result does.
   */
  focusMessage: (messageId: string) => Promise<void>;
  /**
   * Puts a windowed thread back on the newest page, which is what leaving the
   * search does — the design's «„назад“ возвращает переписку в то же место, где
   * её оставили». A no-op on a thread that was never a window.
   */
  endThreadWindow: () => void;
  /**
   * `replyTo` is the message being answered and, when somebody selected part of
   * it rather than answering all of it, the fragment. All four senders take it:
   * a reply can be a line of text, a photo or a кружочек, and the quote belongs
   * to the message rather than to the kind of thing it is.
   *
   * The last argument on all four is where the message goes, and it exists for
   * the one caller that is not the composer: something shared into the app from
   * outside is addressed to a chat that has been picked on a screen of its own,
   * with no thread open behind it. Left off, it is the conversation on screen —
   * which is every other call site, and which is why it is not the first
   * argument.
   */
  sendText: (body: string, replyTo: ReplyTo | null, ai?: AiRequest, chatId?: string) => Promise<void>;
  sendFile: (file: File, replyTo: ReplyTo | null, chatId?: string) => Promise<void>;
  /** A кружочек or a voice message, with the upload's progress on the bubble. */
  sendMedia: (media: OutgoingMedia, replyTo: ReplyTo | null, chatId?: string) => Promise<void>;
  /** A group of photos and videos under one caption, as a single message. */
  sendAlbum: (album: OutgoingAlbum, replyTo: ReplyTo | null, chatId?: string) => Promise<void>;
  /**
   * Copies a message into another chat.
   *
   * No optimistic bubble: the thread it lands in is not the one on screen, and
   * by the time it is — the caller navigates there — the server has answered
   * and the message is in the list the way any other message is. What that buys
   * is that a forward is never a bubble somebody has to watch: it either
   * arrives or it says so, before the screen changes.
   */
  forwardMessage: (messageId: string, chatId: string) => Promise<void>;
  /**
   * What the system «Поделиться» handed this app, or `null`.
   *
   * On the context rather than on the screen that draws it because of when it
   * arrives: the shell has it before the web view has run, and this provider is
   * the first thing that exists afterwards. It also means the login screen
   * costs nothing — the provider is inside RequireAuth, so a share that arrives
   * for a signed-out phone simply waits in the shell until there is somebody to
   * show it to.
   */
  pendingShare: SharedPayload | null;
  /**
   * Done with it, sent or not: the shell may delete the files it copied. Not
   * the same thing as sending — the outbox has its own copies by then.
   */
  clearPendingShare: () => void;
  /**
   * The in-app path a tapped notification asks for, or `null`.
   *
   * Here for the same reason `pendingShare` is: a tap arrives before there is a
   * screen to act on it — before the web view has run at all, when it is what
   * started the app — and this provider is the first thing that exists after
   * one. Turning it into a navigation is `NotificationRedirect`'s job in
   * App.tsx, because the provider deliberately knows nothing about routes.
   */
  pendingNotification: string | null;
  /** Navigated, so the same tap cannot be acted on twice. */
  clearPendingNotification: () => void;
  /**
   * Sends a queued attachment again, by the id its bubble carries.
   *
   * Only ever needed for the sends the outbox has stopped retrying on its own —
   * the ones the server refused rather than the ones the network interrupted.
   */
  retrySend: (clientId: string) => void;
  /** The recipient played a кружочек, which is what greys out its ring. */
  markViewed: (messageId: string) => Promise<void>;
  /**
   * Asks the server to turn a voice message into text. Safe to call for one
   * that is already running or already recognised — the server hands back what
   * it has rather than starting again — so nothing here has to know whether
   * somebody else pressed it first.
   */
  transcribe: (messageId: string) => Promise<void>;
  /** Rewrites one's own text message; the bubble then reads «· изменено». */
  editText: (messageId: string, body: string) => Promise<void>;
  deleteMessage: (messageId: string, scope: DeleteScope) => Promise<void>;
  /** `null` clears this reader's reaction, as does re-picking the same smiley. */
  setReaction: (messageId: string, slug: string | null) => Promise<void>;
  /**
   * Keeps a chat at the top of this list, or lets it go. Optimistic: the row has
   * to move under the finger that asked, and the summary that comes back settles
   * the moment it was pinned at.
   */
  pinChat: (chatId: string, pinned: boolean) => Promise<void>;
  startChat: (nickname: string) => Promise<ChatSummary>;
  /** «Новая группа»: creates it and returns the chat to open. */
  createGroup: (title: string, memberIds: string[]) => Promise<ChatSummary>;
  /**
   * Leaving a group (your own id) or removing somebody from it (theirs). The
   * chat goes out of this list only in the first case; the server decides which
   * one this is and tells the other devices.
   */
  removeMember: (chatId: string, userId: string) => Promise<void>;
  /** Conversations where somebody else is typing right now. */
  typingChatIds: string[];
  /** Called on every keystroke in the composer; the throttling lives here. */
  reportTyping: () => void;
}

const ChatContext = createContext<ChatState | null>(null);

/**
 * Comfortably under the server's 30s expiry, so one dropped beat — a slow
 * reconnect, a moment of throttling — does not read as a backgrounded app.
 */
const FOREGROUND_REFRESH_MS = 10_000;

function statusOf(message: MessageDto): MessageStatus {
  return message.readAt !== null ? 'read' : 'sent';
}

function sortByTime(messages: MessageView[]): MessageView[] {
  return [...messages].sort((left, right) => left.createdAt - right.createdAt);
}

/**
 * Folds a server message into a thread. The same message can arrive twice —
 * once as the POST response, once as this client's own socket echo — so an id
 * that is already present is merged rather than appended, and an optimistic
 * placeholder is dropped once its real counterpart lands.
 */
function applyServerMessage(
  list: MessageView[],
  message: MessageDto,
  options: { live?: boolean; replacing?: string } = {},
): MessageView[] {
  const withoutPlaceholder = options.replacing
    ? list.filter((item) => item.id !== options.replacing)
    : list;

  const existing = withoutPlaceholder.find((item) => item.id === message.id);
  if (existing) {
    // A receipt can arrive before the POST that created the message resolves,
    // and that response was serialised while it was still unread — so a known
    // readAt always wins, or ✓✓ would flicker back to ✓.
    const readAt = message.readAt ?? existing.readAt;
    return withoutPlaceholder.map((item) =>
      item.id === message.id
        ? {
            ...item,
            ...message,
            readAt,
            status: readAt !== null ? 'read' : statusOf(message),
            live: item.live ?? options.live,
          }
        : item,
    );
  }

  return sortByTime([
    ...withoutPlaceholder,
    { ...message, status: statusOf(message), live: options.live },
  ]);
}

/**
 * What the chips will look like once the server agrees, so the reaction appears
 * under the thumb rather than after a round trip. It repeats the server's rule
 * (setReaction in server/src/chats.ts): one reaction per person, picking the
 * one already chosen takes it back, and picking another replaces it.
 */
function nextReactions(current: ReactionView[], slug: string | null): ReactionView[] {
  const withoutMine = current
    .map((reaction) => (reaction.mine ? { ...reaction, count: reaction.count - 1, mine: false } : reaction))
    .filter((reaction) => reaction.count > 0);

  const clearing = slug === null || current.some((reaction) => reaction.mine && reaction.slug === slug);
  if (clearing) return withoutMine;

  const existing = withoutMine.find((reaction) => reaction.slug === slug);
  if (existing) {
    return withoutMine.map((reaction) =>
      reaction.slug === slug ? { ...reaction, count: reaction.count + 1, mine: true } : reaction,
    );
  }
  return [...withoutMine, { slug, count: 1, mine: true }];
}

function summariseForList(message: MessageDto): ChatSummary['lastMessage'] {
  return {
    id: message.id,
    kind: message.kind,
    // The same wording previewOf uses on the server, so an optimistic snippet
    // and the one that comes back from a refresh say the same thing.
    preview: messagePreview(message),
    createdAt: message.createdAt,
    fromMe: message.fromMe,
    callVideo: message.kind === 'call' ? (message.callVideo ?? false) : null,
    // Carried for every chat and rendered only by the group rows, which is
    // where a name in front of the snippet means anything; one's own message
    // never gets one, the same rule the server applies in toChatSummary.
    senderName: message.fromMe ? null : message.senderName,
  };
}

/**
 * `unread` is the caller's judgement rather than something this can work out:
 * whether the message counts against the badge depends on what is on screen at
 * the moment it arrives, and on whether this id has been counted before. Both
 * are the provider's to know — see countOnce, which is the half that keeps a
 * message delivered twice from being counted twice.
 *
 * The count is the one thing here that accumulates, and therefore the one thing
 * a repeat would get wrong; every other field is written rather than added to,
 * which is why nothing else minded.
 */
function withUpdatedPreview(
  chats: ChatSummary[],
  message: MessageDto,
  unread = false,
): ChatSummary[] {
  const updated = chats.map((chat) =>
    chat.id === message.conversationId
      ? {
          ...chat,
          lastMessage: summariseForList(message),
          unreadCount: chat.unreadCount + (unread ? 1 : 0),
        }
      : chat,
  );
  // The same comparator the list itself draws with, borrowed rather than
  // written out again: a message arriving must not float a chat above the ones
  // somebody pinned, and two spellings of that rule would only agree until one
  // of them was edited.
  return updated.sort(pinnedFirst);
}

export function ChatProvider({ children }: { children: ReactNode }) {
  // Only an optimistic bubble needs this: every message that has been anywhere
  // near the server already carries who wrote it.
  const { user } = useAuth();
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [chatsLoading, setChatsLoading] = useState(true);
  const [chatsFailed, setChatsFailed] = useState(false);
  const [connected, setConnected] = useState(false);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [threads, setThreads] = useState<Record<string, MessageView[]>>({});
  const [messagesLoading, setMessagesLoading] = useState(false);
  /** Per chat, because the reader may walk away from a failure into a thread
      that loads perfectly well and must not inherit its state. */
  const [threadFailed, setThreadFailed] = useState<Record<string, boolean>>({});
  /** Conversation id → whether the server still has messages before its first. */
  const [hasMore, setHasMore] = useState<Record<string, boolean>>({});
  const [loadingOlder, setLoadingOlder] = useState(false);
  /**
   * Conversation id → whether the server has messages *after* its last, which
   * is the same thing as «this thread is a window». Only a jump to a search
   * result opens one, and everything that puts the newest page back closes it.
   */
  const [hasNewer, setHasNewer] = useState<Record<string, boolean>>({});
  /** Conversation id → when the peer's «печатает…» lapses if it is not renewed. */
  const [typingUntil, setTypingUntil] = useState<Record<string, number>>({});

  /**
   * Material the system «Поделиться» handed the app, waiting for a chat to be
   * picked for it. Held here rather than on the screen that draws it, because
   * it arrives before that screen exists — often before anybody has signed in.
   */
  const [pendingShare, setPendingShare] = useState<SharedPayload | null>(null);

  /**
   * Where a tapped notification wants to go, waiting for the router.
   *
   * This used to be a `location.assign` on the spot, which was fine while the
   * only tap that could happen was one that had just started the app: there was
   * nothing to lose to a reload. A tap on an app already running is the case
   * that broke — reloading it throws away the socket, the loaded threads and
   * whatever was half-typed in the conversation being left, to arrive somewhere
   * the router could have gone in a frame.
   */
  const [pendingNotification, setPendingNotification] = useState<string | null>(null);

  // Refs mirror the state the socket handlers need, so those handlers can stay
  // subscribed across renders instead of resubscribing on every list change.
  const activeChatIdRef = useRef<string | null>(null);
  activeChatIdRef.current = activeChatId;
  const chatsRef = useRef<ChatSummary[]>([]);
  chatsRef.current = chats;
  // Read by the receipt handler, which has to tell one's own «прочитано» from a
  // fellow group member's — only the first clears the badge.
  const myIdRef = useRef<string | null>(null);
  myIdRef.current = user?.id ?? null;
  // Read by the edit/delete/react actions so an optimistic change knows what to
  // put back on failure, without making them re-created on every new message.
  const threadsRef = useRef<Record<string, MessageView[]>>({});
  threadsRef.current = threads;
  const hasMoreRef = useRef<Record<string, boolean>>({});
  hasMoreRef.current = hasMore;
  // Read by the socket handler, which must not glue an arriving message onto
  // the bottom of a window — that would be this morning stitched to last
  // winter, with the rest of the conversation missing in between.
  const hasNewerRef = useRef<Record<string, boolean>>({});
  hasNewerRef.current = hasNewer;
  // Guards the history fetch against the scroll handler, which fires far faster
  // than the request comes back and would otherwise ask for the same page a
  // dozen times. A ref rather than the state above: this has to be true within
  // the same tick, before React has re-rendered anything.
  const olderPending = useRef(false);
  /** The same guard for the other end of a window. */
  const newerPending = useRef(false);

  const updateThread = useCallback(
    (chatId: string, update: (list: MessageView[]) => MessageView[]) => {
      setThreads((current) => ({ ...current, [chatId]: update(current[chatId] ?? []) }));
    },
    [],
  );

  /**
   * The badge and the shade are one act, which is why they are one function.
   *
   * A notification is removed by a tap and by nothing else, so a conversation
   * read inside the app used to leave its banner standing for good — and on
   * Android the launcher's badge counts banners, so the icon kept saying «1»
   * over a list with nothing unread in it. This is called from the two places
   * that mean «this person has now seen it»: opening the thread, and a receipt
   * from another device of their own, which is what makes reading on a laptop
   * clear the phone's shade.
   *
   * Unconditional, unlike the count above it: the notification may be there
   * when the count is already zero — that is precisely the state being repaired
   * — and removing one that is not there costs nothing.
   */
  const clearUnread = useCallback((chatId: string) => {
    setChats((current) =>
      current.map((chat) => (chat.id === chatId && chat.unreadCount > 0
        ? { ...chat, unreadCount: 0 }
        : chat)),
    );
    void clearNotifications(`chat:${chatId}`);
  }, []);

  /**
   * Ids that have already moved a badge.
   *
   * The guard this replaces compared against the row's current last message,
   * which only ever caught an immediate repeat: A, B, A counted A twice. A set
   * catches the case that actually happens — the same `message:new` arriving
   * over two sockets of one session, or a socket delivering an event the page
   * had already taken from the POST it answered.
   *
   * Bounded by emptying rather than by evicting the oldest: a repeat that turns
   * up five hundred messages later is not the kind anything here guards against,
   * and a session left open for days must not grow a set of everything it has
   * ever seen.
   */
  const counted = useRef<Set<string>>(new Set());
  const countOnce = useCallback((messageId: string) => {
    if (counted.current.has(messageId)) return false;
    if (counted.current.size >= 500) counted.current.clear();
    counted.current.add(messageId);
    return true;
  }, []);

  const clearTyping = useCallback((chatId: string) => {
    setTypingUntil((current) => {
      if (!(chatId in current)) return current;
      return Object.fromEntries(Object.entries(current).filter(([id]) => id !== chatId));
    });
  }, []);

  const refreshChats = useCallback(async () => {
    try {
      setChats(await api.listChats());
      setChatsFailed(false);
    } catch {
      // Offline: keep whatever the list already shows and retry on reconnect.
      // Which is right for a device that has a list, and was the whole screen
      // for one that does not — so the failure is written down rather than only
      // swallowed, and the screen decides what to do with it.
      setChatsFailed(true);
    } finally {
      setChatsLoading(false);
    }
  }, []);

  /**
   * The newest page, replacing whatever was held for this chat — that is what
   * makes it the right thing to run on a reconnect, where the point is to stop
   * trusting what is on screen.
   *
   * Which is also why it asks for as much as it already had: a reader who had
   * scrolled back through four pages should not find the thread cut down to one
   * because the socket blinked.
   */
  const loadMessages = useCallback(async (chatId: string) => {
    const loaded = threadsRef.current[chatId]?.length ?? 0;
    setMessagesLoading(true);
    try {
      const page = await api.listMessages(chatId, loaded > 0 ? { limit: loaded } : {});
      setThreads((current) => ({
        ...current,
        [chatId]: page.messages.map((dto) => ({ ...dto, status: statusOf(dto) })),
      }));
      setHasMore((current) => ({ ...current, [chatId]: page.hasMore }));
      // The newest page by definition, so whatever window was open is closed.
      setHasNewer((current) => ({ ...current, [chatId]: false }));
      setThreadFailed((current) =>
        chatId in current
          ? Object.fromEntries(Object.entries(current).filter(([id]) => id !== chatId))
          : current,
      );
    } catch {
      // Leave the thread as-is; the socket reconnect will retry — and say so,
      // because on a thread that was never loaded «as-is» is a blank
      // conversation that looks exactly like one nobody has written in.
      setThreadFailed((current) => ({ ...current, [chatId]: true }));
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  /**
   * One page further back. The cursor is the oldest message on this device
   * rather than a count of pages, so messages arriving at the bottom while the
   * reader is up in last winter cannot shift what comes next.
   */
  const loadOlderMessages = useCallback(() => {
    const chatId = activeChatIdRef.current;
    if (!chatId || olderPending.current || !hasMoreRef.current[chatId]) return;

    const oldest = threadsRef.current[chatId]?.[0];
    // Nothing to page back from: an empty thread is the newest page's job.
    if (!oldest) return;

    olderPending.current = true;
    setLoadingOlder(true);
    void (async () => {
      try {
        const page = await api.listMessages(chatId, { before: oldest.id });
        setThreads((current) => {
          const list = current[chatId] ?? [];
          // The thread may have been reloaded under this fetch, so a page is
          // merged rather than trusted to be disjoint.
          const known = new Set(list.map((item) => item.id));
          const older = page.messages
            .filter((dto) => !known.has(dto.id))
            .map((dto) => ({ ...dto, status: statusOf(dto) }));
          return { ...current, [chatId]: sortByTime([...older, ...list]) };
        });
        setHasMore((current) => ({ ...current, [chatId]: page.hasMore }));
      } catch {
        // Offline, most likely. hasMore is left standing, so the next scroll
        // up asks again rather than pretending the chat starts here.
      } finally {
        olderPending.current = false;
        setLoadingOlder(false);
      }
    })();
  }, []);

  /**
   * One page further forward — the other half of paging a window, and only ever
   * called inside one. Walking out of a window to the end of the conversation
   * is what turns it back into an ordinary thread: the page that comes back
   * saying there is nothing newer is the page that closes it, and live messages
   * start landing in it again.
   */
  const loadNewerMessages = useCallback(() => {
    const chatId = activeChatIdRef.current;
    if (!chatId || newerPending.current || !hasNewerRef.current[chatId]) return;

    const list = threadsRef.current[chatId];
    const newest = list?.[list.length - 1];
    if (!newest) return;

    newerPending.current = true;
    void (async () => {
      try {
        const page = await api.listMessages(chatId, { after: newest.id });
        setThreads((current) => {
          const held = current[chatId] ?? [];
          const known = new Set(held.map((item) => item.id));
          const newer = page.messages
            .filter((dto) => !known.has(dto.id))
            .map((dto) => ({ ...dto, status: statusOf(dto) }));
          return { ...current, [chatId]: sortByTime([...held, ...newer]) };
        });
        setHasNewer((current) => ({ ...current, [chatId]: page.hasNewer }));
      } catch {
        // Offline, most likely. hasNewer is left standing, so the next scroll
        // down asks again rather than pretending the chat ends here.
      } finally {
        newerPending.current = false;
      }
    })();
  }, []);

  /**
   * Puts the thread on one message — what a tap on a search result does.
   *
   * Nothing to fetch if it is already on screen: the thread scrolls to it and
   * the flash says which one it was. Otherwise the window replaces the thread,
   * and from then until it is walked or closed this conversation is showing a
   * middle rather than an end.
   */
  const focusMessage = useCallback(async (messageId: string) => {
    const chatId = activeChatIdRef.current;
    if (!chatId) return;
    if (threadsRef.current[chatId]?.some((message) => message.id === messageId)) return;

    setMessagesLoading(true);
    try {
      const page = await api.listMessages(chatId, { around: messageId });
      setThreads((current) => ({
        ...current,
        [chatId]: page.messages.map((dto) => ({ ...dto, status: statusOf(dto) })),
      }));
      setHasMore((current) => ({ ...current, [chatId]: page.hasMore }));
      setHasNewer((current) => ({ ...current, [chatId]: page.hasNewer }));
    } catch {
      // The thread stays where it was; the result row simply did not open.
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  const endThreadWindow = useCallback(() => {
    const chatId = activeChatIdRef.current;
    if (!chatId || !hasNewerRef.current[chatId]) return;
    void loadMessages(chatId);
  }, [loadMessages]);

  /**
   * The two «Повторить» buttons, which are the same two fetches the provider
   * makes on mount. Deliberately not a retry loop of their own: a device with
   * no signal would spend a battery on one, and the person looking at the
   * screen knows better than any timer when it is worth another try.
   */
  const retryChats = useCallback(() => {
    setChatsLoading(true);
    void refreshChats();
  }, [refreshChats]);

  const retryMessages = useCallback(() => {
    const chatId = activeChatIdRef.current;
    if (chatId) void loadMessages(chatId);
  }, [loadMessages]);

  useEffect(() => {
    void refreshChats();
  }, [refreshChats]);

  // On every launch, not just the one where notifications were turned on: iOS
  // discards push subscriptions without telling the app, so the server's copy
  // has to be re-asserted or notifications quietly stop arriving forever.
  useEffect(() => {
    void syncSubscription();
  }, []);

  // What the notifications on this phone have already caused, collected from
  // the native side (see client/src/lib/nativePush.ts). On the web the service
  // worker does both halves of this itself and posts NOTIFICATION_CLICK below;
  // in the app there is no worker, iOS draws the notification from the APNs
  // payload, and this is where a tap finally becomes a navigation.
  //
  // On mount and on every return to the foreground, because a tap that
  // cold-starts the app and a tap that resumes it are the same event queued at
  // two different moments — and a WKWebView does fire visibilitychange when the
  // app comes back, so no extra plugin is needed to hear about it.
  //
  // And on the shell saying so, which is the third moment and the one neither of
  // those covers: a banner tapped from the shade over a running app never
  // backgrounds it, so nothing above fires and the tap sat in the queue until
  // the next time the app happened to leave the screen. See watchNativePushEvents.
  useEffect(() => {
    const drain = () => {
      void drainNativeNotifications().then((urls) => {
        // The last one, not the first: several notifications may be waiting,
        // and the one somebody just tapped is the one they meant to open.
        const target = urls[urls.length - 1];
        if (target) setPendingNotification(target);
      });
      // A ring is a question that expires, and being in the app is the answer:
      // whatever the call turned into, the socket has already said so (see
      // deliverPending in server/src/realtime.ts) and there is nothing left in
      // the shade to act on. Cancelling one is CallKit's business on an iPhone
      // and nobody's on Android, where a missed call otherwise left a banner —
      // and therefore a badge — standing until somebody swiped it away.
      void clearNotifications('call');

      // Its own queue beside that one, because a queue drains once: two
      // consumers of a single queue would each get half of what arrived. What
      // is waiting here is material somebody handed the app from outside, and
      // this is the moment it becomes a screen — including the moment right
      // after a sign-in, since nothing in this provider exists before one.
      // Re-stated beside the drain rather than once at sign-in: the iOS share
      // sheet posts the message itself and needs this session's bearer, and a
      // credential re-written on every return costs nothing next to reasoning
      // about when it last changed.
      void configureNativeShare();
      void drainNativeShares().then((arrived) => {
        // The last one, for the same reason the notification above is: several
        // could be waiting after a spell offline, and the one just shared is
        // the one somebody is standing in front of. The rest are let go of
        // rather than queued behind it — a screen asking about a photograph
        // from an hour ago is worse than not asking.
        const latest = arrived[arrived.length - 1];
        for (const stale of arrived.slice(0, -1)) void releaseNativeShare(stale.id);
        if (latest) setPendingShare(latest);
      });
    };

    drain();
    const onVisible = () => {
      if (document.visibilityState === 'visible') drain();
    };
    document.addEventListener('visibilitychange', onVisible);
    const unwatch = watchNativePushEvents(drain);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      unwatch();
    };
  }, []);

  useEffect(() => {
    if (activeChatId) void loadMessages(activeChatId);
  }, [activeChatId, loadMessages]);

  // ---- live connection ----

  useEffect(() => {
    chatSocket.connect();
    return () => chatSocket.disconnect();
  }, []);

  // The server suppresses a push only for the conversation on screen, which it
  // can only know because of this. `focus`/`blur` cover another window taking
  // over on a desktop; `visibilitychange` covers the phone being locked or the
  // app being swiped away.
  //
  // Reporting only on those events is not enough, and getting that wrong is what
  // broke push the first time round: a phone suspends the process the instant it
  // is backgrounded, so the `false` frequently never leaves the device and the
  // server goes on believing the app is open. The server therefore expires the
  // claim, and this repeats it on a timer so that staying silent is what means
  // "gone" — the one signal a suspended app cannot fake.
  //
  // Read through a ref rather than closed over, so that opening a conversation
  // does not tear down and re-register five listeners — and so the cleanup's
  // `leave()` keeps meaning "logout" rather than firing on every navigation
  // between threads. The effect below re-reports when the id changes.
  const watchingRef = useRef<string | null>(null);
  watchingRef.current = activeChatId ?? null;
  const reportForeground = useRef<(() => void) | null>(null);

  useEffect(() => {
    const report = () => chatSocket.setForeground(isOnScreen(), watchingRef.current);
    const leave = () => chatSocket.setForeground(false, null);

    report();
    const keepalive = window.setInterval(report, FOREGROUND_REFRESH_MS);

    document.addEventListener('visibilitychange', report);
    window.addEventListener('focus', report);
    window.addEventListener('blur', report);
    // Fired when a page is frozen or put into the back/forward cache, which on
    // mobile is often the only warning before the process stops running.
    window.addEventListener('pagehide', leave);
    document.addEventListener('freeze', leave);
    reportForeground.current = report;

    return () => {
      window.clearInterval(keepalive);
      document.removeEventListener('visibilitychange', report);
      window.removeEventListener('focus', report);
      window.removeEventListener('blur', report);
      window.removeEventListener('pagehide', leave);
      document.removeEventListener('freeze', leave);
      reportForeground.current = null;
      // Leaving the chat UI entirely (logout) must not leave the server
      // believing this user is still watching.
      leave();
    };
  }, []);

  // Leaving a thread for the chat list has to reach the server now rather than
  // at the next keepalive tick: for those seconds the claim would still name a
  // conversation nobody is looking at, and a message arriving in it would be
  // swallowed as a duplicate of something that is no longer on screen.
  useEffect(() => {
    reportForeground.current?.();
    // And the same fact to the native half, which needs it for the opposite
    // decision: the server has already refused to push a message to whoever is
    // reading that same thread, so what reaches a phone in the foreground
    // belongs to another conversation and must be shown. Without this the app
    // could only show all of them or none, and it showed none.
    void reportActiveConversation(activeChatId);
  }, [activeChatId]);

  // A push that arrived while the app was open and focused. The service worker
  // hands it over instead of showing an OS notification (see client/src/sw.ts).
  //
  // There is nothing to do with it today: chatSocket is the live channel, and
  // the same message has already arrived over it as `message:new` — which is
  // why this only logs. A future payload that the socket does not carry (a call
  // invitation, say) would be dispatched from here instead.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; payload?: unknown; url?: string } | null;
      // Which conversation is on screen, asked at the moment a push arrives
      // rather than remembered. A worker woken by a push is a fresh worker with
      // no memory, so anything the page told it earlier is gone — and a stale
      // answer here is a notification wrongly swallowed, which is the bug this
      // exchange exists to fix. The reply goes back down the port it came on;
      // the worker shows the notification anyway if nobody answers.
      if (data?.type === 'WHICH_CHAT') {
        event.ports[0]?.postMessage({ conversationId: activeChatIdRef.current });
        return;
      }
      if (data?.type === 'PUSH_WHILE_ACTIVE') {
        console.debug('push received while app active; handled in-app', data.payload);
        return;
      }
      // Tapping a notification when a window is already open focuses it rather
      // than opening a second one, so the navigation happens here.
      if (data?.type === 'NOTIFICATION_CLICK' && typeof data.url === 'string') {
        setPendingNotification(data.url);
        return;
      }
      // The browser finished an upload this page had handed over, and the worker
      // has already posted the message. Nudging the outbox is what turns the
      // bubble from «отправляется» into a sent one without waiting for the next
      // time the app is opened.
      if (data?.type === 'OUTBOX_SETTLED') void outbox.resume();
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);

  useEffect(
    () =>
      chatSocket.onStatus((isConnected) => {
        setConnected(isConnected);
        if (!isConnected) return;
        // A reconnect may have missed messages entirely — resync rather than
        // trusting what is on screen.
        void refreshChats();
        const active = activeChatIdRef.current;
        if (active) void loadMessages(active);
      }),
    [refreshChats, loadMessages],
  );

  useEffect(
    () =>
      chatSocket.onEvent((event: ServerEvent) => {
        if (event.type === 'message:new') {
          const { message } = event;
          // Not into a window: the thread on screen stops somewhere in the
          // middle of the conversation, and appending today's message to it
          // would draw it directly under one from months ago. The chat list
          // below still learns about it, which is where it shows up until the
          // reader comes back to the end.
          if (!hasNewerRef.current[message.conversationId]) {
            updateThread(message.conversationId, (list) =>
              applyServerMessage(list, message, { live: true }),
            );
          }
          // They typed it and sent it: «печатает…» is over, and waiting for it
          // to lapse would leave it up beside the message it produced.
          if (!message.fromMe) clearTyping(message.conversationId);
          // The same condition the read receipt below is sent under, so the two
          // never disagree: a message that will be marked read on arrival never
          // bumps the badge for a frame, and one arriving into a backgrounded
          // tab bumps it even though the thread it belongs to is open.
          const unread =
            !message.fromMe &&
            !(message.conversationId === activeChatIdRef.current && isOnScreen()) &&
            countOnce(message.id);
          setChats((current) => withUpdatedPreview(current, message, unread));
          // A message in a conversation this client has never seen (the peer
          // wrote first) — the list has to learn about it.
          if (!chatsRef.current.some((chat) => chat.id === message.conversationId)) {
            void refreshChats();
          }
          return;
        }

        if (event.type === 'message:edited') {
          updateThread(event.conversationId, (list) =>
            list.map((item) => {
              if (item.id === event.messageId) {
                return { ...item, body: event.body, editedAt: event.editedAt };
              }
              // A quote is the original as it reads now, not as it read when it
              // was answered — which a reload gets for free (replyViewOf) and an
              // open thread has to be told.
              if (item.replyTo?.id !== event.messageId || item.replyTo.deleted) return item;
              return {
                ...item,
                replyTo: {
                  ...item.replyTo,
                  preview: quotePreview(event.body, item.replyTo.quote),
                },
              };
            }),
          );
          // The snippet under the nickname may have been quoting the old text.
          void refreshChats();
          return;
        }

        if (event.type === 'message:deleted') {
          updateThread(event.conversationId, (list) =>
            list
              .filter((item) => item.id !== event.messageId)
              // The replies to it stay; their quotes say what became of it. Only
              // «у всех» does that — hiding one's own copy of a message is no
              // reason to blank what somebody else's reply is answering.
              .map((item) =>
                event.forEveryone && item.replyTo?.id === event.messageId
                  ? { ...item, replyTo: { ...item.replyTo, preview: DELETED_QUOTE, deleted: true } }
                  : item,
              ),
          );
          // Deleting the newest message changes what the list should show, and
          // the client cannot work out the new last message on its own — the
          // thread it holds may not go back far enough.
          void refreshChats();
          return;
        }

        if (event.type === 'message:reaction') {
          updateThread(event.conversationId, (list) =>
            list.map((item) =>
              item.id === event.messageId ? { ...item, reactions: event.reactions } : item,
            ),
          );
          return;
        }

        if (event.type === 'message:viewed') {
          updateThread(event.conversationId, (list) =>
            list.map((item) =>
              item.id === event.messageId ? { ...item, viewedAt: event.viewedAt } : item,
            ),
          );
          return;
        }

        if (event.type === 'message:transcript') {
          updateThread(event.conversationId, (list) =>
            list.map((item) =>
              item.id === event.messageId ? { ...item, transcript: event.transcript } : item,
            ),
          );
          return;
        }

        if (event.type === 'message:read') {
          updateThread(event.conversationId, (list) =>
            list.map((item) =>
              event.messageIds.includes(item.id)
                ? { ...item, status: 'read', readAt: event.readAt }
                : item,
            ),
          );
          // What another device of one's own read is read here too — this is the
          // only thing that keeps a phone and a laptop showing the same badge.
          // Somebody else's receipt is about their ✓✓ and nothing more.
          if (event.readerId === myIdRef.current) clearUnread(event.conversationId);
          return;
        }

        if (event.type === 'presence') {
          setChats((current) =>
            current.map((chat) =>
              chat.peer?.id === event.userId
                ? { ...chat, online: event.online, lastSeenAt: event.lastSeenAt ?? chat.lastSeenAt }
                : chat,
            ),
          );
          // Someone who just went offline is not still typing. The TTL would
          // catch this a few seconds later; «не в сети · печатает…» in the
          // meantime is nonsense worth avoiding.
          if (!event.online) {
            for (const chat of chatsRef.current) {
              if (chat.peer?.id === event.userId) clearTyping(chat.id);
            }
          }
          return;
        }

        if (event.type === 'typing') {
          if (event.typing) {
            setTypingUntil((current) => ({
              ...current,
              [event.conversationId]: Date.now() + TYPING_TTL_MS,
            }));
          } else {
            clearTyping(event.conversationId);
          }
          return;
        }

        if (event.type === 'conversation:new') {
          setChats((current) =>
            current.some((chat) => chat.id === event.chat.id) ? current : [event.chat, ...current],
          );
          return;
        }

        if (event.type === 'conversation:updated') {
          setChats((current) =>
            current.map((chat) => (chat.id === event.chat.id ? event.chat : chat)),
          );
          return;
        }

        if (event.type === 'conversation:removed') {
          setChats((current) => current.filter((chat) => chat.id !== event.conversationId));
          // The thread goes with it: leaving a group has to take its messages
          // off this device, not leave them behind a route nobody can reach.
          setThreads((current) =>
            Object.fromEntries(
              Object.entries(current).filter(([id]) => id !== event.conversationId),
            ),
          );
          setHasMore((current) =>
            Object.fromEntries(
              Object.entries(current).filter(([id]) => id !== event.conversationId),
            ),
          );
          clearTyping(event.conversationId);
        }
      }),
    [updateThread, refreshChats, clearTyping, clearUnread, countOnce],
  );

  // ---- typing ----

  /**
   * The indicator is expired here rather than waited out: a claim is only ever
   * valid until TYPING_TTL_MS after the last time it was asserted, so a peer
   * whose phone locked mid-word stops «печатает…» on its own. One timer, armed
   * for whichever claim lapses first.
   */
  useEffect(() => {
    const soonest = Math.min(...Object.values(typingUntil));
    if (!Number.isFinite(soonest)) return;

    const timer = window.setTimeout(
      () => {
        const now = Date.now();
        setTypingUntil((current) => {
          const live = Object.entries(current).filter(([, until]) => until > now);
          // Same object when nothing lapsed, or this effect would re-arm forever.
          return live.length === Object.keys(current).length ? current : Object.fromEntries(live);
        });
      },
      Math.max(0, soonest - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [typingUntil]);

  /**
   * Which conversation a claim belongs to is pinned here rather than read from
   * activeChatIdRef when it is sent: leaving a chat mid-word has to end the
   * indicator in the chat that was left, not in the one just opened.
   */
  const typingChatRef = useRef<string | null>(null);
  const reporter = useMemo(
    () =>
      new TypingReporter((typing) => {
        const chatId = typingChatRef.current;
        if (chatId) chatSocket.setTyping(chatId, typing);
      }),
    [],
  );

  const reportTyping = useCallback(() => {
    const chatId = activeChatIdRef.current;
    if (!chatId) return;
    if (typingChatRef.current !== chatId) {
      // Ends it in the previous conversation first, while the ref still names it.
      reporter.stop();
      typingChatRef.current = chatId;
    }
    reporter.keystroke();
  }, [reporter]);

  useEffect(() => {
    const timer = window.setInterval(() => reporter.tick(), TYPING_HEARTBEAT_MS);
    return () => {
      window.clearInterval(timer);
      reporter.stop();
    };
  }, [reporter]);

  // Switching conversations, or leaving the chat UI, ends the claim now rather
  // than letting the other person watch it lapse.
  useEffect(() => () => reporter.stop(), [activeChatId, reporter]);

  const typingChatIds = useMemo(() => Object.keys(typingUntil), [typingUntil]);

  // ---- the outbox ----

  /**
   * The queue as this render sees it.
   *
   * The queue itself lives in a module rather than in React, so that leaving
   * this provider's tree cannot interrupt an upload; this is a copy of it that
   * a render can read, refreshed whenever it moves.
   */
  const [outgoing, setOutgoing] = useState<outbox.PendingMessage[]>([]);

  useEffect(() => outbox.subscribe(() => setOutgoing(outbox.snapshot())), []);

  // Resumes whatever the last session left unfinished. Here rather than at the
  // top of the app because a queued send belongs to an account: the uploads it
  // is carrying on with are only authorised while that account is signed in.
  useEffect(() => {
    void outbox.start();
  }, []);

  /**
   * A queued send that has landed, folded into its thread as the real message.
   *
   * Registered once and for the whole app, not per open conversation: the send
   * that finishes is very often not the one on screen — that is the entire
   * point of the outbox — and the chat list has to learn about it too.
   */
  useEffect(
    () =>
      outbox.onSettled((message) => {
        updateThread(message.conversationId, (list) => applyServerMessage(list, message));
        setChats((current) => withUpdatedPreview(current, message));
      }),
    [updateThread],
  );

  // ---- read receipts ----

  /**
   * The open conversation, with anything still going up in it.
   *
   * The two halves are kept apart right until here, and that is what fixes the
   * bug this was all built for: `loadMessages` replaces a thread wholesale with
   * the server's page every time the conversation is opened, so a pending
   * bubble kept in `threads` was wiped by walking out of the chat and back in —
   * while the upload it was drawing carried on invisibly. Merged at read time,
   * it cannot be: the queue is not the server's to overwrite.
   *
   * A pending send whose real message is already in the thread is dropped
   * rather than drawn twice. That window is small and entirely real: the
   * socket's echo of one's own message can arrive before the outbox has
   * finished putting its copy away.
   */
  const activeThread = useMemo(() => {
    if (!activeChatId) return [];
    const settled = threads[activeChatId] ?? [];
    const landed = new Set(settled.map((message) => message.clientId).filter(Boolean));
    const pending = outgoing.filter(
      (message) => message.conversationId === activeChatId && !landed.has(message.id),
    );
    return pending.length === 0 ? settled : sortByTime([...settled, ...pending]);
  }, [activeChatId, threads, outgoing]);

  const lastIncomingId = useMemo(() => {
    for (let index = activeThread.length - 1; index >= 0; index -= 1) {
      const message = activeThread[index]!;
      if (!message.fromMe) return message.id;
    }
    return null;
  }, [activeThread]);

  useEffect(() => {
    if (!activeChatId || !connected) return;

    const report = () => {
      // The same test the foreground claim above is made under, and they have
      // to stay the same one: a window that is visible but not focused used to
      // mark a message read here while telling the server it was not being
      // looked at, so the notification went out for a message this very client
      // had just settled. See lib/onScreen.ts.
      if (!isOnScreen()) return;
      chatSocket.markRead(activeChatId);
      // Locally as well as over the socket: the badge on the row you just opened
      // should go now, not a round trip later. The receipt confirms the same.
      clearUnread(activeChatId);
    };
    report();
    document.addEventListener('visibilitychange', report);
    // `focus` for the same reason the claim listens to it: on a desktop a window
    // can come forward without ever having been hidden, and that moment is both
    // the start of a foreground claim and the moment the thread on screen is
    // genuinely being read.
    window.addEventListener('focus', report);
    return () => {
      document.removeEventListener('visibilitychange', report);
      window.removeEventListener('focus', report);
    };
    // lastIncomingId re-runs this when a new message arrives while the thread is open.
  }, [activeChatId, connected, lastIncomingId, clearUnread]);

  // ---- sending ----

  const send = useCallback(
    async (chatId: string, post: (clientId: string) => Promise<MessageDto>, draft: MessageView) => {
      // The message says everything «печатает…» was saying.
      reporter.stop();
      updateThread(chatId, (list) => sortByTime([...list, draft]));
      try {
        const saved = await post(draft.id);
        updateThread(chatId, (list) =>
          applyServerMessage(list, saved, { replacing: draft.id }),
        );
        setChats((current) => withUpdatedPreview(current, saved));
      } catch {
        updateThread(chatId, (list) =>
          list.map((item) => (item.id === draft.id ? { ...item, status: 'failed' } : item)),
        );
      }
    },
    [updateThread, reporter],
  );

  /**
   * The quote an optimistic bubble carries until the server sends its own copy
   * down. Built from the thread this device already holds, so a reply appears
   * complete rather than growing a quote a round trip later.
   */
  const quoteFor = useCallback((chatId: string, replyTo: ReplyTo | null) => {
    if (replyTo === null) return null;
    const target = (threadsRef.current[chatId] ?? []).find((item) => item.id === replyTo.id);
    return target ? quoteMessage(target, replyTo.quote) : null;
  }, []);

  const sendText = useCallback(
    async (body: string, replyTo: ReplyTo | null, ai?: AiRequest, into?: string) => {
      const chatId = into ?? activeChatIdRef.current;
      const trimmed = body.trim();
      if (!chatId || !trimmed) return;

      const clientId = crypto.randomUUID();
      await send(
        chatId,
        (id) => api.sendText(chatId, trimmed, id, replyTo, ai),
        {
          id: clientId,
          conversationId: chatId,
          fromMe: true,
          senderId: user?.id ?? '',
          senderName: user?.displayName ?? '',
          senderAvatarId: user?.avatarPhotoId ?? null,
          kind: 'text',
          body: trimmed,
          fileName: null,
          fileSize: null,
          mime: null,
          createdAt: Date.now(),
          readAt: null,
          editedAt: null,
          durationMs: null,
          trimStartMs: null,
          trimEndMs: null,
          viewedAt: null,
          // The divider and the refusals are written by the server; the one
          // mark a person's own bubble can carry is «повтор» — stamped here so
          // the label is on the bubble from its first frame, and by the server
          // so it survives a reload.
          aiNotice: ai?.redo ? 'redo' : null,
          aiImage: null,
          // Nothing anyone sends is a call; the record for one is written by the
          // server when the call ends and arrives as an ordinary message:new.
          callResult: null,
          callVideo: null,
          reactions: [],
          replyTo: quoteFor(chatId, replyTo),
          // Nothing typed is a forward: the one thing that carries this comes
          // from forwardMessage, which draws no optimistic bubble at all.
          forwardedFrom: null,
          transcript: null,
          attachments: [],
          status: 'sending',
        },
      );
    },
    [send, quoteFor, user],
  );

  /**
   * The optimistic bubble every attachment starts as.
   *
   * Built here and handed to the outbox, which persists it: the bubble has to
   * be redrawable by a session that never saw the send begin, so it can carry
   * nothing that only exists in this page — no object URLs, no blobs, no
   * reference to a component. Those belong to the outbox and are attached back
   * on when it hands the pending message out again.
   */
  const draftFor = useCallback(
    (chatId: string, clientId: string, kind: MessageDto['kind'], fields: Partial<MessageDto>): MessageDto => ({
      id: clientId,
      conversationId: chatId,
      fromMe: true,
      senderId: user?.id ?? '',
      senderName: user?.displayName ?? '',
      senderAvatarId: user?.avatarPhotoId ?? null,
      kind,
      body: null,
      fileName: null,
      fileSize: null,
      mime: null,
      createdAt: Date.now(),
      readAt: null,
      editedAt: null,
      durationMs: null,
      trimStartMs: null,
      trimEndMs: null,
      viewedAt: null,
      aiNotice: null,
      aiImage: null,
      // Nothing anyone sends is a call; the record for one is written by the
      // server when the call ends and arrives as an ordinary message:new.
      callResult: null,
      callVideo: null,
      reactions: [],
      // Overridden by every caller that has a reply target; null is the answer
      // for a message said on its own.
      replyTo: null,
      // A forward is written by the server out of a message that already
      // exists, so nothing the outbox carries is ever one.
      forwardedFrom: null,
      // Nothing is recognised before it has been uploaded; auto mode starts on
      // the server once the row exists, and arrives over the socket.
      transcript: null,
      // Empty until the server answers with the stored tiles; an album's bubble
      // draws the outbox's own previews in the meantime.
      attachments: [],
      ...fields,
    }),
    [user],
  );

  /**
   * A document or a picture from the file picker.
   *
   * The three senders below all do the same two things and nothing else: draw
   * the bubble, and hand the bytes to the outbox. None of them awaits an
   * upload, because none of them owns one — see chat/outbox.ts for where a send
   * actually lives and why it is not here.
   */
  const sendFile = useCallback(
    async (file: File, replyTo: ReplyTo | null, into?: string) => {
      const chatId = into ?? activeChatIdRef.current;
      if (!chatId) return;

      const clientId = crypto.randomUUID();
      reporter.stop();
      await outbox.queueFile(
        chatId,
        clientId,
        file,
        replyTo,
        draftFor(
          chatId,
          clientId,
          // Matches the server's rule, so the optimistic bubble already has the
          // right shape while the bytes are still in flight.
          file.type.startsWith('image/') && file.type !== 'image/svg+xml' ? 'image' : 'file',
          { fileName: file.name, fileSize: file.size, mime: file.type, replyTo: quoteFor(chatId, replyTo) },
        ),
      );
    },
    [draftFor, quoteFor, reporter],
  );

  /**
   * A recording. Everything about it is known before the upload starts — the
   * length, the shape of the bubble, the still frame — so the кружочек appears
   * whole and simply fills up, rather than arriving as a placeholder.
   */
  const sendMedia = useCallback(
    async (media: OutgoingMedia, replyTo: ReplyTo | null, into?: string) => {
      const chatId = into ?? activeChatIdRef.current;
      if (!chatId) return;

      const clientId = crypto.randomUUID();
      reporter.stop();
      await outbox.queueMedia(
        chatId,
        clientId,
        media,
        replyTo,
        draftFor(chatId, clientId, media.kind, {
          fileName: media.fileName,
          fileSize: media.blob.size,
          mime: media.blob.type,
          durationMs: media.durationMs,
          trimStartMs: media.trimStartMs,
          trimEndMs: media.trimEndMs,
          replyTo: quoteFor(chatId, replyTo),
        }),
      );
    },
    [draftFor, quoteFor, reporter],
  );

  /**
   * A group of photos and videos, as one message.
   *
   * The bubble that appears is the finished one — the same grid, laid out from
   * the same table, drawn from the files on this device. Ten photographs take a
   * while to go up, and watching nine grey squares fill a progress bar would be
   * the one part of sending them that felt slow.
   *
   * The object URLs behind those tiles are handed over with the files: the
   * outbox owns them from here, revokes them when the send lands, and makes new
   * ones from its own copies of the bytes if the app is closed and reopened
   * while the group is still going up.
   *
   * A group carrying video has a phase before all of that: the clips are
   * re-encoded on this device (see lib/videoCompress.ts, and the numbers for why
   * a phone's own file is far too big for a chat). Those seconds are counted
   * separately and under their own verb, because they are not upload seconds —
   * nothing is going out yet, and the file that eventually does is a fraction of
   * what was picked.
   */
  const sendAlbum = useCallback(
    async (album: OutgoingAlbum, replyTo: ReplyTo | null, into?: string) => {
      const chatId = into ?? activeChatIdRef.current;
      if (!chatId || album.items.length === 0) return;

      const clientId = crypto.randomUUID();
      reporter.stop();
      await outbox.queueAlbum(
        chatId,
        clientId,
        album,
        replyTo,
        draftFor(chatId, clientId, 'album', {
          // The caption lives where a text message's words live, which is what
          // makes an album's chat-list snippet and its push preview work without
          // either of them learning a new field.
          body: album.caption.trim() || null,
          replyTo: quoteFor(chatId, replyTo),
        }),
      );
    },
    [draftFor, quoteFor, reporter],
  );

  /**
   * Copies a message into another chat.
   *
   * Not through the outbox and not through `send`, because there is nothing to
   * queue and nothing to draw: the words and the bytes are already on the
   * server, so this is one small POST that either answers with a message or
   * throws. The caller — the picker — is what turns a throw into a sentence on
   * screen, and it stays on the list rather than opening the chat when it does.
   *
   * A `clientId` all the same, so the one failure that is not a failure is
   * covered: a request whose answer was lost, retried by a finger, would
   * otherwise put the message in twice.
   */
  const forwardMessage = useCallback(
    async (messageId: string, chatId: string) => {
      const saved = await api.forwardMessage(chatId, messageId, crypto.randomUUID());
      // Exactly what the socket echo of this message does a moment later, and
      // by id, so the two cannot produce two bubbles — this is only here so the
      // thread and the list are right on a device whose socket is down.
      if (!hasNewerRef.current[chatId]) {
        updateThread(chatId, (list) => applyServerMessage(list, saved));
      }
      setChats((current) => withUpdatedPreview(current, saved));
    },
    [updateThread],
  );

  const clearPendingShare = useCallback(() => {
    setPendingShare((current) => {
      if (current) void releaseNativeShare(current.id);
      return null;
    });
  }, []);

  const clearPendingNotification = useCallback(() => setPendingNotification(null), []);

  const retrySend = useCallback((clientId: string) => outbox.retry(clientId), []);

  /**
   * Stamped optimistically: the ring greys out under the thumb, and the server
   * is told afterwards. It refuses a second stamp and a sender's own note, so
   * a request that turns out to be redundant simply changes nothing.
   */
  const markViewed = useCallback(
    async (messageId: string) => {
      const chatId = activeChatIdRef.current;
      if (!chatId) return;

      const message = (threadsRef.current[chatId] ?? []).find((item) => item.id === messageId);
      if (!message || message.fromMe || message.viewedAt !== null) return;

      updateThread(chatId, (list) =>
        list.map((item) => (item.id === messageId ? { ...item, viewedAt: Date.now() } : item)),
      );
      try {
        await api.markViewed(chatId, messageId);
      } catch {
        // Watched is watched. Leaving the ring grey on this device and letting
        // the next load settle it beats flicking it back to accent mid-playback.
      }
    },
    [updateThread],
  );

  /**
   * «Расшифровать». The spinner appears under the thumb rather than after a
   * round trip, and what the server answers replaces it — normally the same
   * `pending`, but a message somebody else already had recognised comes back
   * finished, with no socket event to wait for.
   *
   * A refusal puts the bubble back the way it was: an engine that has gone away
   * since sign-in, or a recording past the length limit, leaves a button to
   * press again rather than a spinner that will never stop.
   */
  const transcribe = useCallback(
    async (messageId: string) => {
      const chatId = activeChatIdRef.current;
      if (!chatId) return;

      const previous = (threadsRef.current[chatId] ?? []).find((item) => item.id === messageId);
      if (!previous || previous.kind !== 'voice') return;
      // Already running: the press was a toggle of the block, not a second job.
      if (previous.transcript?.status === 'pending') return;

      const setTranscript = (transcript: MessageView['transcript']) =>
        updateThread(chatId, (list) =>
          list.map((item) => (item.id === messageId ? { ...item, transcript } : item)),
        );

      setTranscript({ status: 'pending', text: null, at: null });
      try {
        setTranscript(await api.transcribe(chatId, messageId));
      } catch {
        setTranscript(previous.transcript);
      }
    },
    [updateThread],
  );

  // ---- editing, deleting, reacting ----

  /**
   * All three are optimistic in the same shape as sending: change what is on
   * screen now, tell the server, and put it back if it refuses. The socket
   * echo arrives afterwards and is what the other device acts on; here it
   * lands on a thread that already agrees with it.
   */
  const editText = useCallback(
    async (messageId: string, body: string) => {
      const chatId = activeChatIdRef.current;
      const trimmed = body.trim();
      if (!chatId || !trimmed) return;

      const previous = (threadsRef.current[chatId] ?? []).find((item) => item.id === messageId);
      updateThread(chatId, (list) =>
        list.map((item) =>
          item.id === messageId ? { ...item, body: trimmed, editedAt: Date.now() } : item,
        ),
      );

      try {
        await api.editMessage(chatId, messageId, trimmed);
      } catch {
        if (previous) {
          updateThread(chatId, (list) =>
            list.map((item) => (item.id === messageId ? previous : item)),
          );
        }
      }
    },
    [updateThread],
  );

  const deleteMessage = useCallback(
    async (messageId: string, scope: DeleteScope) => {
      const chatId = activeChatIdRef.current;
      if (!chatId) return;

      const previous = threadsRef.current[chatId] ?? [];
      updateThread(chatId, (list) => list.filter((item) => item.id !== messageId));

      try {
        await api.deleteMessage(chatId, messageId, scope);
        await refreshChats();
      } catch {
        // Nothing was deleted server-side, so putting the whole thread back is
        // both correct and simpler than working out where the message went.
        updateThread(chatId, () => previous);
      }
    },
    [updateThread, refreshChats],
  );

  const setReaction = useCallback(
    async (messageId: string, slug: string | null) => {
      const chatId = activeChatIdRef.current;
      if (!chatId) return;

      const previous = (threadsRef.current[chatId] ?? []).find((item) => item.id === messageId);
      const optimistic = nextReactions(previous?.reactions ?? [], slug);
      updateThread(chatId, (list) =>
        list.map((item) => (item.id === messageId ? { ...item, reactions: optimistic } : item)),
      );

      try {
        const reactions = await api.setReaction(chatId, messageId, slug);
        updateThread(chatId, (list) =>
          list.map((item) => (item.id === messageId ? { ...item, reactions } : item)),
        );
      } catch {
        if (previous) {
          updateThread(chatId, (list) =>
            list.map((item) =>
              item.id === messageId ? { ...item, reactions: previous.reactions } : item,
            ),
          );
        }
      }
    },
    [updateThread],
  );

  const pinChat = useCallback(async (chatId: string, pinned: boolean) => {
    const previous = chatsRef.current.find((chat) => chat.id === chatId)?.pinnedAt ?? null;
    // Date.now() rather than nothing: the row has to take its place among the
    // other pins immediately, and its place is decided by this number. The
    // server's own is a millisecond or two later and replaces it below.
    const guess = pinned ? Date.now() : null;
    const apply = (at: number | null) =>
      setChats((current) =>
        current.map((chat) => (chat.id === chatId ? { ...chat, pinnedAt: at } : chat)),
      );

    apply(guess);
    try {
      const chat = await api.pinChat(chatId, pinned);
      apply(chat.pinnedAt);
    } catch {
      // The list is somebody's own, so a pin the server refused has to come back
      // off rather than sit there looking saved.
      apply(previous);
    }
  }, []);

  const startChat = useCallback(async (nickname: string) => {
    const chat = await api.startChat(nickname);
    setChats((current) =>
      current.some((item) => item.id === chat.id) ? current : [chat, ...current],
    );
    return chat;
  }, []);

  const createGroup = useCallback(async (title: string, memberIds: string[]) => {
    const chat = await api.createGroup(title, memberIds);
    // At the top of the list, where a chat with no messages would otherwise
    // sort last — a group you have just made is the one you want to open.
    setChats((current) => [chat, ...current.filter((item) => item.id !== chat.id)]);
    return chat;
  }, []);

  /**
   * The server answers 204 and then tells every device what happened, so the
   * list is left to the `conversation:removed` / `conversation:updated` events
   * rather than changed here: leaving and being removed have to look the same
   * on the other four screens, and there is exactly one place that decides.
   */
  const removeMember = useCallback(async (chatId: string, userId: string) => {
    await api.removeMember(chatId, userId);
  }, []);

  const value = useMemo(
    () => ({
      chats,
      chatsLoading,
      chatsFailed,
      retryChats,
      connected,
      activeChatId,
      setActiveChatId,
      messages: activeThread,
      messagesLoading,
      messagesReady: activeChatId !== null && activeChatId in threads,
      messagesFailed: activeChatId !== null && threadFailed[activeChatId] === true,
      retryMessages,
      hasMoreMessages: activeChatId !== null && hasMore[activeChatId] === true,
      loadingOlderMessages: loadingOlder,
      loadOlderMessages,
      hasNewerMessages: activeChatId !== null && hasNewer[activeChatId] === true,
      loadNewerMessages,
      focusMessage,
      endThreadWindow,
      sendText,
      sendFile,
      sendMedia,
      sendAlbum,
      forwardMessage,
      pendingShare,
      clearPendingShare,
      pendingNotification,
      clearPendingNotification,
      retrySend,
      markViewed,
      transcribe,
      editText,
      deleteMessage,
      setReaction,
      pinChat,
      startChat,
      createGroup,
      removeMember,
      typingChatIds,
      reportTyping,
    }),
    [
      chats,
      chatsLoading,
      chatsFailed,
      retryChats,
      connected,
      activeChatId,
      activeThread,
      threads,
      messagesLoading,
      threadFailed,
      retryMessages,
      hasMore,
      loadingOlder,
      loadOlderMessages,
      hasNewer,
      loadNewerMessages,
      focusMessage,
      endThreadWindow,
      sendText,
      sendFile,
      sendMedia,
      sendAlbum,
      forwardMessage,
      pendingShare,
      clearPendingShare,
      pendingNotification,
      clearPendingNotification,
      retrySend,
      markViewed,
      transcribe,
      editText,
      deleteMessage,
      setReaction,
      pinChat,
      startChat,
      createGroup,
      removeMember,
      typingChatIds,
      reportTyping,
    ],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat(): ChatState {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used inside ChatProvider');
  return ctx;
}
